/**
 * CalendarsPage — CRUD cho bảng market_calendar (Sprint 5).
 * Type MarketCalendar: composite PK `code` + `ex_text`.
 * Permission: purchase.refs.calendar EDIT — RoleGuard.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm } from 'react-hook-form'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import DateTextField from '@/components/DateTextField'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import RoleGuard from '@/components/RoleGuard'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useListCalendarsQuery,
  useCreateCalendarMutation,
  useUpdateCalendarMutation,
  useDeleteCalendarMutation,
} from '@/store/api/refsCrudApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import type { MarketCalendar } from '@/types/api'

interface CalFormValues {
  code: string
  ex_text: string
  first_notice_day: string
  last_trading_day: string
}

const EMPTY: CalFormValues = { code: '', ex_text: '', first_notice_day: '', last_trading_day: '' }

export function CalendarsPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editing, setEditing] = useState<MarketCalendar | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<MarketCalendar | null>(null)

  const { data, isLoading, error, isFetching } = useListCalendarsQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createCalendar, { isLoading: creating }] = useCreateCalendarMutation()
  const [updateCalendar, { isLoading: updating }] = useUpdateCalendarMutation()
  const [deleteCalendar, { isLoading: deleting }] = useDeleteCalendarMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const { register, handleSubmit, reset, setValue, watch } = useForm<CalFormValues>({ defaultValues: EMPTY })
  const firstNoticeDay = watch('first_notice_day')
  const lastTradingDay = watch('last_trading_day')

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        code: editing.code,
        ex_text: editing.ex_text,
        first_notice_day: editing.first_notice_day ?? '',
        last_trading_day: editing.last_trading_day ?? '',
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((r) => ({ id: `${r.code}::${r.ex_text}`, ...r })),
    [data],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'ex_text', headerName: 'Exchange', width: 160 },
      { field: 'first_notice_day', headerName: 'First Notice Day', width: 160 },
      { field: 'last_trading_day', headerName: 'Last Trading Day', width: 160 },
      {
        field: '_actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(params.row as MarketCalendar) }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setConfirmDelete(params.row as MarketCalendar) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => { setCreateOpen(false); setEditing(null) }

  const onSubmit = handleSubmit(async (values) => {
    const payload: MarketCalendar = {
      code: values.code.trim(),
      ex_text: values.ex_text.trim(),
      first_notice_day: values.first_notice_day || null,
      last_trading_day: values.last_trading_day || null,
    }
    try {
      if (isEdit && editing) {
        await updateCalendar({ code: editing.code, ex_text: editing.ex_text, body: payload }).unwrap()
        snack.success(`Updated calendar "${editing.code}|${editing.ex_text}"`)
      } else {
        await createCalendar(payload).unwrap()
        snack.success(`Created calendar "${payload.code}|${payload.ex_text}"`)
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Update failed' : 'Create failed'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteCalendar({ code: confirmDelete.code, ex_text: confirmDelete.ex_text }).unwrap()
      snack.success(`Deleted "${confirmDelete.code}|${confirmDelete.ex_text}"`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete failed'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>Market Calendars</Typography>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => setCreateOpen(true)}>
          New Calendar
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TextField
          label="Search"
          placeholder="Futures month..."
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })) }}
          sx={{ minWidth: 260 }}
        />
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as MarketCalendar)}
            getRowId={(row) => `${(row as MarketCalendar).code}::${(row as MarketCalendar).ex_text}`}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Edit Calendar' : 'New Calendar'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="Code *" size="small" fullWidth {...register('code')} disabled={isEdit} placeholder="e.g. H25" />
          <TextField label="Exchange *" size="small" fullWidth {...register('ex_text')} disabled={isEdit} placeholder="e.g. ICE US" />
          <input type="hidden" {...register('first_notice_day')} />
          <input type="hidden" {...register('last_trading_day')} />
          <DateTextField
            label="First Notice Day"
            size="small"
            fullWidth
            value={firstNoticeDay}
            onChange={(value) => setValue('first_notice_day', value, { shouldDirty: true, shouldTouch: true })}
          />
          <DateTextField
            label="Last Trading Day"
            size="small"
            fullWidth
            value={lastTradingDay}
            onChange={(value) => setValue('last_trading_day', value, { shouldDirty: true, shouldTouch: true })}
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Calendar"
        message={confirmDelete ? `Are you sure you want to delete calendar "${confirmDelete.code}|${confirmDelete.ex_text}"?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default CalendarsPage
